import { useRef } from 'react'
import { useSelector } from 'react-redux'
import { I18n, Translate } from 'react-redux-i18n'
import useGetVsic from '../../../../../../common/category/useGetVsic'
import { Legend } from '../../../../../../common/chart/legend'
import { PieChartContainer } from '../../../../../../common/chart/pieChart'
import { getUnique } from '../../../../../../helpers/common'
import { formatVal, valToPercent } from '../../../../../../helpers/value'
import {
  selectCompareIndustry,
  selectDefaultIndustry,
} from '../../../store/slice'
import { KEY_DATA, MAP_TYPE_TITLE } from '../../constant'
import { selectCompareVal, selectData } from '../../store/slice'

const ChartNumberOfCompanies = ({ isDynamicReport, settingDynamic = {} }) => {
  const chartId = useRef(getUnique())
  const data = useSelector(selectData)
  const { vsics } = useGetVsic()
  const vsicCompareStore = useSelector(selectCompareIndustry)
  const vsicDefaultStore = useSelector(selectDefaultIndustry)
  const compareValStore = useSelector(selectCompareVal)
  const locale = useSelector((state) => state.i18n.locale)

  const {
    DEFAULT_INDUSTRY: vsicDefaultDyn,
    COMPARE_INDUSTRY: vsicCompareDyn,
    compareBetweenRatio: compareValDyn,
  } = settingDynamic

  const vsicDefault = isDynamicReport ? vsicDefaultDyn : vsicDefaultStore
  const vsicCompare = isDynamicReport ? vsicCompareDyn : vsicCompareStore
  const compareVal = isDynamicReport ? compareValDyn : compareValStore

  const defaultData = data[compareVal]?.defaultIndustry?.noc
  const comparingData = data[compareVal]?.comparingIndustry?.noc
  const titleDefaultIndustry = vsics.find(
    (item) => item.id === vsicDefault,
  )?.name
  const titleComparingIndustry = vsics.find(
    (item) => item.id === vsicCompare,
  )?.name

  const legend = [
    {
      title: MAP_TYPE_TITLE[compareVal][KEY_DATA.VALUE_1],
      dataKey: KEY_DATA.VALUE_1,
      color: '#006AA6',
      pieIndex: 0,
    },
    {
      title: MAP_TYPE_TITLE[compareVal][KEY_DATA.VALUE_2],
      dataKey: KEY_DATA.VALUE_2,
      color: '#FFCD60',
      pieIndex: 1,
    },
  ]

  const formatData = (dataSource) => {
    if (!dataSource || !dataSource.length) return []

    const totalCount = dataSource.reduce(
      (sum, item) => sum + (item.count || 0),
      0,
    )

    return dataSource.map((item, index) => {
      const percent = totalCount ? (item.count / totalCount) * 100 : 0
      return {
        ...item,
        value: item.count,
        color: legend[index]?.color,
        name: I18n.t(legend[index]?.title),
        totalCount,
        percent: Number(percent.toFixed(2)),
      }
    })
  }

  const formatValueTooltip = (_, { activeData }) => {
    return `${formatVal(activeData.value, locale, false, 0)} | ${valToPercent(
      activeData.percent,
      locale,
      true,
    )}`
  }

  return (
    <div className="w100" id="numberOfCompaniesCharId">
      <div
        className="dFlex gap4 w100 justifyContentCenter"
        style={{ marginTop: isDynamicReport ? 0 : 40 }}
      >
        <div className="dFlex flexColumn alignItemsCenter">
          <PieChartContainer
            chartId={chartId.current}
            data={formatData(defaultData)}
            width={160}
            height={160}
            innerRadius={40}
            formatValueTooltip={formatValueTooltip}
            isLabelActive={false}
            labelCenter={{
              customLabel: () => (
                <TranslateCompanies
                  count={formatData(defaultData)?.[0]?.totalCount}
                />
              ),
              outerRadius: '50%',
            }}
          />
          <div
            className="dFlex justifyContentCenter mt1"
            style={{ maxWidth: 400 }}
          >
            {titleDefaultIndustry}
          </div>
        </div>
        {vsicCompare ? (
          <>
            <div className="dFlex flexColumn alignItemsCenter">
              <PieChartContainer
                chartId={chartId.current}
                data={formatData(comparingData)}
                width={160}
                height={160}
                innerRadius={40}
                formatValueTooltip={formatValueTooltip}
                isLabelActive={false}
                labelCenter={{
                  customLabel: () => (
                    <TranslateCompanies
                      count={formatData(comparingData)?.[0]?.totalCount}
                    />
                  ),
                  outerRadius: '50%',
                }}
              />
              <div
                className="dFlex justifyContentCenter mt1"
                style={{ maxWidth: 400 }}
              >
                {titleComparingIndustry}
              </div>
            </div>
          </>
        ) : null}
      </div>
      <Legend
        chartId={chartId.current}
        items={legend}
        className="justifyContentCenter gap4 pt4"
      />
    </div>
  )
}

export default ChartNumberOfCompanies

const TranslateCompanies = ({ count, props }) => {
  const locale = useSelector((state) => state.i18n.locale)
  const formatCount = formatVal(count, locale, false, 0)

  if (!count) {
    return <></>
  }

  if (count < 2) {
    return (
      <Translate
        value="companyAnalysis.industryAnalysis.COMPANY"
        count={formatCount}
        {...props}
      />
    )
  }
  return (
    <Translate
      value="companyAnalysis.industryAnalysis.COMPANIES"
      count={formatCount}
      {...props}
    />
  )
}
